/**
 * Client bundle for the /docs surface.
 *
 * What this does:
 * - Mobile sidebar drawer toggle (mirrors the `data-drawer-open` attribute
 *   on `<body class="docs-body">` that `src/styles/docs.css` keys off).
 * - Right-rail TOC active-link highlight via `IntersectionObserver`.
 * - Search overlay — opens on Cmd/Ctrl+K and a header toggle, lazily loads
 *   the MiniSearch index, and renders keyboard-navigable results.
 *
 * Why this file exists:
 * - The SSR shell at `src/ssr/docs/shell.ts` renders a functional page
 *   without any JS. This bundle is progressive enhancement: the sidebar
 *   has a mobile open/close toggle, headings get a scroll-spy highlight,
 *   and keyboard users get search.
 *
 * Related files:
 * - `src/ssr/docs/shell.ts` loads this via `/static/docs.js`.
 * - `src/styles/docs.css` owns the matching style surface.
 * - `README/Plans/growth/174-docs-and-help-center-plan.md` — implementation
 *   plan of record for the docs/help surfaces.
 */

import { track } from './web-telemetry.js';

interface DocsState {
  drawerOpen: boolean;
}

const state: DocsState = { drawerOpen: false };

/**
 * Selector constants.
 *
 * Kept together so a rename in `docs/shell.ts` lands here in one diff.
 */
const Selectors = {
  BODY: 'body.docs-body',
  SIDEBAR: '[data-docs-sidebar]',
  HEADER: '.docs-header',
  TOC_LINKS: '[data-docs-toc-link]',
  SEARCH_ROOT: '#docs-search-root'
} as const;

/**
 * Data-attribute names. Mirrored in `src/styles/docs.css` — any rename
 * must update both files together.
 */
const DataAttrs = {
  DRAWER_OPEN: 'data-drawer-open',
  TOC_ACTIVE: 'data-active'
} as const;

/**
 * Find the docs main heading for each TOC slug so the IntersectionObserver
 * can flip the active link as the user scrolls.
 *
 * @param slugs — headings declared in the article's manifest `toc`.
 */
function findHeadings(slugs: string[]): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const slug of slugs) {
    const el = document.getElementById(slug);
    if (el instanceof HTMLElement) out.push(el);
  }
  return out;
}

/**
 * Wire the scroll-spy highlight. Only active when the article has a TOC
 * (i.e. on article pages; landing/category pages skip this cleanly).
 */
function initTocHighlight(): void {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(Selectors.TOC_LINKS));
  if (links.length === 0) return;
  const slugs = links
    .map((link) => link.getAttribute('data-docs-toc-link'))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
  const headings = findHeadings(slugs);
  if (headings.length === 0) return;

  const slugToLink = new Map<string, HTMLAnchorElement>();
  for (const link of links) {
    const slug = link.getAttribute('data-docs-toc-link');
    if (slug) slugToLink.set(slug, link);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      // Pick the topmost intersecting heading; on scroll-down we want the
      // section that just entered viewport.
      let topmost: IntersectionObserverEntry | null = null;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!topmost || entry.boundingClientRect.top < topmost.boundingClientRect.top) {
          topmost = entry;
        }
      }
      if (!topmost) return;
      const id = topmost.target.id;
      for (const link of links) {
        if (link.getAttribute('data-docs-toc-link') === id) {
          link.setAttribute(DataAttrs.TOC_ACTIVE, 'true');
        } else {
          link.removeAttribute(DataAttrs.TOC_ACTIVE);
        }
      }
    },
    {
      rootMargin: '-80px 0px -60% 0px',
      threshold: [0, 0.5, 1]
    }
  );

  for (const h of headings) observer.observe(h);
}

/**
 * Wire the mobile sidebar drawer. On screens ≤900 px the sidebar is hidden
 * by default; a button in the header toggles the drawer.
 */
function initSidebarDrawer(): void {
  const header = document.querySelector<HTMLElement>(Selectors.HEADER);
  const sidebar = document.querySelector<HTMLElement>(Selectors.SIDEBAR);
  if (!header || !sidebar) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'docs-sidebar-toggle';
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', 'docs-sidebar-contents');
  button.innerHTML = '<span aria-hidden="true">☰</span><span class="sr-only">Toggle navigation</span>';
  // Place the toggle at the left edge of the header for thumb reach.
  header.insertBefore(button, header.firstChild);

  sidebar.id = 'docs-sidebar-contents';

  const apply = (open: boolean) => {
    state.drawerOpen = open;
    document.body.setAttribute(DataAttrs.DRAWER_OPEN, open ? 'true' : 'false');
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  button.addEventListener('click', () => apply(!state.drawerOpen));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.drawerOpen) apply(false);
  });
  // Close the drawer when a sidebar link is clicked, so the page the user
  // just tapped actually becomes visible instead of being covered by the
  // still-open drawer on top of it.
  sidebar.addEventListener('click', (e) => {
    const target = e.target;
    if (target instanceof HTMLElement && target.closest('.docs-sidebar-link')) {
      apply(false);
    }
  });
}

/**
 * Shape of each stored search record, mirroring the generator's storeFields.
 *
 * Must match `emitSearchIndex()` in `scripts/build-docs.mjs` — if a field
 * changes there, update this interface + the result renderer below.
 */
interface DocsSearchHit {
  slug: string;
  title: string;
  summary: string;
  section: 'docs';
  category: string;
  score: number;
}

/** URL at which the prebuilt MiniSearch JSON asset is served. */
const SEARCH_INDEX_URL = '/static/docs-search-index.json';
/** Max hits to render in the overlay. */
const SEARCH_RESULT_LIMIT = 12;

/**
 * Escape a user query for safe interpolation into HTML attributes.
 *
 * `innerHTML` is how we render the overlay; the title/summary strings
 * come from the prebuilt index which was already sanitized by
 * rehype-sanitize at build time, but the user's query string is not and
 * flows into placeholder renders.
 */
function escapeHtmlClient(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Mount the search overlay and wire the MiniSearch query pipeline.
 *
 * The overlay lazy-loads the prebuilt index on first input, renders ranked
 * results grouped into section badges, and supports Cmd/Ctrl+K, Esc,
 * Up/Down, and Enter.
 */
function initSearchOverlay(): void {
  const host = document.querySelector<HTMLElement>(Selectors.SEARCH_ROOT);
  if (!host) return;

  const navTarget = document.querySelector<HTMLElement>('.docs-header-nav');
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'docs-search-trigger';
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.innerHTML = 'Search <kbd style="margin-left:8px;padding:1px 6px;border:1px solid #E8E8E8;border-radius:4px;font-size:11px;color:#666;">⌘K</kbd>';
  if (navTarget) navTarget.insertBefore(trigger, navTarget.firstChild);

  const overlay = document.createElement('div');
  overlay.className = 'docs-search-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Search Gist docs');
  overlay.innerHTML = `
    <div class="docs-search-dialog">
      <input class="docs-search-input" type="search" placeholder="Search docs…" aria-label="Search" autocomplete="off" spellcheck="false" />
      <div class="docs-search-results" role="listbox" aria-label="Search results">
        <p class="docs-search-empty">Start typing to search the docs.</p>
      </div>
    </div>`;
  host.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>('.docs-search-input');
  const resultsEl = overlay.querySelector<HTMLDivElement>('.docs-search-results');
  if (!input || !resultsEl) return;

  /** Mutable state: index loader, cached results, selection cursor. */
  let indexPromise: Promise<{ search: (q: string) => DocsSearchHit[] } | null> | null = null;
  let currentHits: DocsSearchHit[] = [];
  let selectedIndex = 0;
  let querySequence = 0;
  let queryAnalyticsTimer: number | null = null;
  let lastQueryAnalyticsSignature = '';

  const getIndex = (): Promise<{ search: (q: string) => DocsSearchHit[] } | null> => {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      try {
        const [{ default: MiniSearch }, indexResponse] = await Promise.all([
          import('minisearch'),
          fetch(SEARCH_INDEX_URL, { credentials: 'omit' })
        ]);
        if (!indexResponse.ok) throw new Error(`search index fetch ${indexResponse.status}`);
        // `loadJSON` takes the raw JSON string and the original index options.
        // These options must match `emitSearchIndex()` in scripts/build-docs.mjs.
        const indexJson = await indexResponse.text();
        const mini = MiniSearch.loadJSON(indexJson, {
          fields: ['title', 'summary', 'plainText', 'category', 'section'],
          storeFields: ['slug', 'title', 'summary', 'section', 'category'],
          searchOptions: { boost: { title: 3, summary: 2 } }
        });
        return {
          search(q: string): DocsSearchHit[] {
            // Enable prefix + fuzzy matching per-query so the index remains
            // compatible with the generator's constructor options.
            const raw = mini.search(q, { prefix: true, fuzzy: 0.2 });
            const hits: DocsSearchHit[] = [];
            for (const r of raw) {
              hits.push({
                slug: String(r['slug'] ?? ''),
                title: String(r['title'] ?? ''),
                summary: String(r['summary'] ?? ''),
                section: 'docs',
                category: String(r['category'] ?? ''),
                score: typeof r.score === 'number' ? r.score : 0
              });
            }
            return hits.slice(0, SEARCH_RESULT_LIMIT);
          }
        };
      } catch (err) {
        // Swallow; render the empty-state fallback so the UI is honest.
        return null;
      }
    })();
    return indexPromise;
  };

  const renderEmpty = () => {
    resultsEl.innerHTML = '<p class="docs-search-empty">Start typing to search the docs.</p>';
    currentHits = [];
    selectedIndex = 0;
  };

  const renderUnavailable = () => {
    resultsEl.innerHTML = '<p class="docs-search-empty">Search is offline right now. Try the <a href="/docs">docs index</a> or <a href="/help">contact support</a>.</p>';
    currentHits = [];
    selectedIndex = 0;
  };

  const renderNoResults = (query: string) => {
    resultsEl.innerHTML = `<p class="docs-search-empty">No matches for "${escapeHtmlClient(query)}". Try a simpler keyword or <a href="/help">contact support</a>.</p>`;
    currentHits = [];
    selectedIndex = 0;
  };

  const hrefForHit = (hit: DocsSearchHit): string => `/${hit.section}/${hit.category}/${hit.slug}`;

  const renderHits = (hits: DocsSearchHit[]) => {
    currentHits = hits;
    selectedIndex = 0;
    const items = hits
      .map((hit, i) => {
        const href = hrefForHit(hit);
        return `<a class="docs-search-result" role="option" aria-selected="${i === 0 ? 'true' : 'false'}" href="${escapeHtmlClient(href)}" data-docs-result-index="${i}">
          <span class="docs-search-result-title">
            <span>${escapeHtmlClient(hit.title)}</span>
          </span>
          <span class="docs-search-result-summary">${escapeHtmlClient(hit.summary)}</span>
        </a>`;
      })
      .join('');
    resultsEl.innerHTML = items;
  };

  const scheduleQueryAnalytics = (query: string, status: 'results' | 'none' | 'offline', resultCount: number) => {
    const detail = {
      q_length: query.length,
      result_count: resultCount,
      status
    };
    const signature = `${detail.q_length}:${detail.result_count}:${detail.status}`;
    if (signature === lastQueryAnalyticsSignature) return;
    if (queryAnalyticsTimer !== null) window.clearTimeout(queryAnalyticsTimer);
    queryAnalyticsTimer = window.setTimeout(() => {
      lastQueryAnalyticsSignature = signature;
      queryAnalyticsTimer = null;
      track('docs_search_query', detail);
    }, 600);
  };

  const cancelQueryAnalytics = () => {
    if (queryAnalyticsTimer !== null) {
      window.clearTimeout(queryAnalyticsTimer);
      queryAnalyticsTimer = null;
    }
  };

  const trackResultOpen = (hit: DocsSearchHit, index: number, method: 'click' | 'keyboard') => {
    track('docs_search_result_open', {
      section: hit.section,
      category: hit.category,
      slug: hit.slug,
      position: index + 1,
      method
    });
  };

  const highlightSelection = () => {
    const nodes = resultsEl.querySelectorAll<HTMLAnchorElement>('.docs-search-result');
    nodes.forEach((node, i) => {
      if (i === selectedIndex) {
        node.setAttribute('aria-selected', 'true');
        node.scrollIntoView({ block: 'nearest' });
      } else {
        node.setAttribute('aria-selected', 'false');
      }
    });
  };

  const setOpen = (open: boolean) => {
    overlay.setAttribute('data-open', open ? 'true' : 'false');
    if (open) {
      track('docs_search_open', {});
      requestAnimationFrame(() => input.focus());
    } else {
      querySequence++;
      cancelQueryAnalytics();
      input.value = '';
      renderEmpty();
    }
  };

  const handleQuery = async (raw: string) => {
    const requestId = ++querySequence;
    const q = raw.trim();
    if (q.length === 0) {
      cancelQueryAnalytics();
      renderEmpty();
      return;
    }
    const idx = await getIndex();
    if (requestId !== querySequence || input.value.trim() !== q) return;
    if (!idx) {
      renderUnavailable();
      scheduleQueryAnalytics(q, 'offline', 0);
      return;
    }
    const hits = idx.search(q);
    if (hits.length === 0) {
      renderNoResults(q);
      scheduleQueryAnalytics(q, 'none', 0);
      return;
    }
    renderHits(hits);
    scheduleQueryAnalytics(q, 'results', hits.length);
  };

  trigger.addEventListener('click', () => setOpen(true));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) setOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    const isMeta = e.metaKey || e.ctrlKey;
    if (isMeta && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setOpen(overlay.getAttribute('data-open') !== 'true');
      return;
    }
    if (overlay.getAttribute('data-open') !== 'true') return;
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' && currentHits.length > 0) {
      e.preventDefault();
      selectedIndex = (selectedIndex + 1) % currentHits.length;
      highlightSelection();
    } else if (e.key === 'ArrowUp' && currentHits.length > 0) {
      e.preventDefault();
      selectedIndex = (selectedIndex - 1 + currentHits.length) % currentHits.length;
      highlightSelection();
    } else if (e.key === 'Enter' && currentHits.length > 0) {
      const hit = currentHits[selectedIndex];
      if (hit) {
        e.preventDefault();
        trackResultOpen(hit, selectedIndex, 'keyboard');
        window.location.href = hrefForHit(hit);
      }
    }
  });

  resultsEl.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    const link = target.closest<HTMLAnchorElement>('.docs-search-result');
    if (!link) return;
    const index = Number(link.getAttribute('data-docs-result-index'));
    const hit = Number.isInteger(index) ? currentHits[index] : null;
    if (hit) trackResultOpen(hit, index, 'click');
  });

  input.addEventListener('input', () => {
    void handleQuery(input.value);
  });
}

function bootstrap(): void {
  initTocHighlight();
  initSidebarDrawer();
  initSearchOverlay();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
